/**
 * SectionForm - build TinyMCE dialog forms from the section manifest.
 *
 * Sections are stored in page content as shortcodes:
 *   {partial: "shared/supported_getaways", params: {limit: 3, region: "all"}}
 *
 * Typing that by hand is not something a content editor should ever have to do.
 * config/shortcodes.yml declares each section's parameters, and this module turns
 * that declaration into a dialog: a text box, a dropdown of real programs, a
 * media picker, and so on.
 *
 * Parsing and serialising both happen server-side (see
 * WpsPages::Admin::ShortcodesController#parse/#serialize) so the form can never
 * disagree with the parser that actually renders the page. This file only maps
 * a schema onto dialog widgets and back.
 */

const SECTIONS_ENDPOINT = '/admin/shortcodes/sections';
const PARSE_ENDPOINT = '/admin/shortcodes/parse';
const SERIALIZE_ENDPOINT = '/admin/shortcodes/serialize';

// Separates a multi-select param name from one of its option values, so the flat
// dialog data ({"programs::adventures": true}) can be folded back into an array.
const MULTI_SEPARATOR = '::';

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || '';
}

async function postJSON(url, payload) {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken()
        },
        body: JSON.stringify(payload)
    });

    if (!response.ok) throw new Error(`Request failed (${response.status})`);
    return response.json();
}

export async function fetchSections() {
    const response = await fetch(SECTIONS_ENDPOINT, {
        headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) throw new Error(`Could not load sections (${response.status})`);
    const body = await response.json();
    return body.categories || [];
}

export function parseShortcode(shortcode) {
    return postJSON(PARSE_ENDPOINT, { shortcode });
}

export function serializeShortcode(partial, params) {
    return postJSON(SERIALIZE_ENDPOINT, { partial, params });
}

/**
 * Dialog body items for a section's parameter schema.
 */
export function buildDialogItems(schemaParams, { description } = {}) {
    const items = [];

    if (description) {
        items.push({ type: 'htmlpanel', html: `<p style="margin:0 0 8px;color:#666">${escapeHtml(description)}</p>` });
    }

    if (!schemaParams || schemaParams.length === 0) {
        items.push({
            type: 'htmlpanel',
            html: '<p style="margin:0;color:#666">This section has no settings — its content is managed elsewhere in the admin.</p>'
        });
        return items;
    }

    schemaParams.forEach((param) => {
        const label = param.required ? `${param.label} *` : param.label;
        // Shown greyed-out in an empty field, so an editor can see the shape of
        // the value (or the copy the partial falls back to) without typing one.
        const placeholder = param.placeholder ? {placeholder: param.placeholder} : {};

        switch (param.type) {
            case 'text':
            case 'json':
                items.push({ type: 'textarea', name: param.name, label, ...placeholder });
                break;

            case 'combo':
                // A free-text field with type-ahead over a known list - used where
                // the value must usually be one of the existing records (a form
                // name) but hand-typing one that doesn't exist yet is still valid.
                //
                // TinyMCE has no combobox, so this is a plain input plus a native
                // <datalist>; attachComboInputs() wires the two together once the
                // dialog is in the DOM.
                items.push({ type: 'input', name: param.name, label, ...placeholder });
                items.push({
                    type: 'htmlpanel',
                    html: `<datalist id="${comboListId(param.name)}">` +
                        (param.options || []).map((option) =>
                            `<option value="${escapeHtml(option.value)}">${escapeHtml(option.label)}</option>`
                        ).join('') +
                        '</datalist>'
                });
                break;

            case 'boolean':
                items.push({ type: 'checkbox', name: param.name, label });
                break;

            case 'media':
                // urlinput renders a browse button wired to file_picker_callback,
                // which opens the existing media library picker.
                // urlinput has no placeholder in its spec, so a media param's
                // hint has to go in its `help` text instead.
                items.push({ type: 'urlinput', name: param.name, label, filetype: 'image' });
                break;

            case 'select':
                if (param.multiple) {
                    // TinyMCE dialogs have no multi-select, so a checkbox per option.
                    items.push({ type: 'label', label, items: (param.options || []).map((option) => ({
                        type: 'checkbox',
                        name: `${param.name}${MULTI_SEPARATOR}${option.value}`,
                        label: option.label
                    })) });
                } else {
                    items.push({
                        type: 'selectbox',
                        name: param.name,
                        label,
                        items: selectItems(param)
                    });
                }
                break;

            default:
                items.push({ type: 'input', name: param.name, label, ...placeholder });
        }

        if (param.help) {
            items.push({ type: 'htmlpanel', html: `<p style="margin:-8px 0 8px;font-size:12px;color:#777">${escapeHtml(param.help)}</p>` });
        }
    });

    return items;
}

/**
 * Turn saved shortcode params into the flat shape a TinyMCE dialog expects.
 */
export function toDialogData(schemaParams, params = {}) {
    const data = {};

    (schemaParams || []).forEach((param) => {
        const current = params[param.name];
        const value = current === undefined ? param.default : current;

        switch (param.type) {
            case 'boolean':
                data[param.name] = value === true || value === 'true';
                break;

            case 'json':
                data[param.name] = value === undefined || value === null
                    ? ''
                    : (typeof value === 'string' ? value : JSON.stringify(value, null, 2));
                break;

            case 'select':
                if (param.multiple) {
                    const selected = toArray(value).map(String);
                    (param.options || []).forEach((option) => {
                        data[`${param.name}${MULTI_SEPARATOR}${option.value}`] = selected.includes(String(option.value));
                    });
                } else {
                    data[param.name] = value === undefined || value === null ? '' : String(value);
                }
                break;

            case 'media':
                // urlinput's value is an object, not a string. Handing it a bare
                // string makes windowManager.open throw midway through rendering,
                // which leaves a half-built dialog stranded in the DOM.
                // fromDialogData already reads {value} back out.
                data[param.name] = {
                    value: value === undefined || value === null ? '' : String(value),
                    meta: {}
                };
                break;

            default:
                data[param.name] = value === undefined || value === null ? '' : String(value);
        }
    });

    return data;
}

/**
 * Fold dialog data back into shortcode params.
 *
 * Values equal to the declared default are dropped, so shortcodes stay short and
 * a section keeps following its default if that default later changes.
 */
export function fromDialogData(schemaParams, data = {}) {
    const params = {};

    (schemaParams || []).forEach((param) => {
        let value;

        switch (param.type) {
            case 'boolean':
                value = data[param.name] === true;
                break;

            case 'integer': {
                const raw = String(data[param.name] ?? '').trim();
                if (raw === '') { value = undefined; break; }
                const parsed = parseInt(raw, 10);
                value = Number.isNaN(parsed) ? undefined : parsed;
                break;
            }

            case 'json': {
                const raw = String(data[param.name] ?? '').trim();
                if (raw === '') { value = undefined; break; }
                try {
                    value = JSON.parse(raw);
                } catch (e) {
                    // Keep the author's text rather than silently dropping it; the
                    // server will report it if it can't be used.
                    value = raw;
                }
                break;
            }

            case 'select':
                if (param.multiple) {
                    value = (param.options || [])
                        .filter((option) => data[`${param.name}${MULTI_SEPARATOR}${option.value}`] === true)
                        .map((option) => option.value);
                    if (value.length === 0) value = undefined;
                } else {
                    value = emptyToUndefined(data[param.name]);
                }
                break;

            case 'media':
                // urlinput returns {value, meta}
                value = emptyToUndefined(data[param.name]?.value ?? data[param.name]);
                break;

            default:
                value = emptyToUndefined(data[param.name]);
        }

        if (value === undefined) return;
        if (param.default !== undefined && sameValue(value, param.default)) return;

        params[param.name] = value;
    });

    return params;
}

/**
 * Names of required params the author left empty.
 */
export function missingRequired(schemaParams, params) {
    return (schemaParams || [])
        .filter((param) => param.required)
        .filter((param) => {
            const value = params[param.name];
            return value === undefined || value === null || value === '' ||
                (Array.isArray(value) && value.length === 0);
        })
        .map((param) => param.label || param.name);
}

/**
 * Point every `combo` param's input at the <datalist> rendered beside it.
 *
 * Call after windowManager.open(): the dialog spec has no way to set arbitrary
 * attributes on an input, so the two halves can only be joined once TinyMCE has
 * rendered them. Inputs are found via their label's `for`, which is the only
 * stable link between a dialog field and its DOM node.
 */
export function attachComboInputs(schemaParams) {
    const dialogs = document.querySelectorAll('.tox-dialog');
    const dialog = dialogs[dialogs.length - 1];
    if (!dialog) return;

    (schemaParams || []).filter((param) => param.type === 'combo').forEach((param) => {
        const wanted = param.required ? `${param.label} *` : param.label;
        const label = [...dialog.querySelectorAll('label.tox-label')]
            .find((el) => el.textContent.trim() === wanted);
        if (!label) return;

        const input = label.htmlFor
            ? dialog.querySelector(`#${CSS.escape(label.htmlFor)}`)
            : label.parentElement?.querySelector('input');
        if (!input) return;

        input.setAttribute('list', comboListId(param.name));
        // Chrome hides the datalist behind its own autofill suggestions otherwise.
        input.setAttribute('autocomplete', 'off');
    });
}

function comboListId(name) {
    return `wps-combo-${String(name).replace(/[^a-zA-Z0-9_-]/g, '-')}`;
}

function selectItems(param) {
    const options = (param.options || []).map((option) => ({
        text: option.label,
        value: String(option.value)
    }));

    // A leading blank lets an optional dropdown be cleared back to its default.
    return param.required ? options : [{ text: '(default)', value: '' }, ...options];
}

function toArray(value) {
    if (value === undefined || value === null || value === '') return [];
    return Array.isArray(value) ? value : [value];
}

// String() flattens every object to "[object Object]", so an edited list of
// {label, url} entries compared equal to a default of the same length and was
// dropped as "unchanged". Compare structured values by their JSON instead.
function sameValue(value, fallback) {
    if (typeof value === 'object' || typeof fallback === 'object') {
        return JSON.stringify(value) === JSON.stringify(fallback);
    }
    return String(value) === String(fallback);
}

function emptyToUndefined(value) {
    const string = String(value ?? '').trim();
    return string === '' ? undefined : string;
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = String(text ?? '');
    return div.innerHTML;
}
